import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { SUPPORTED_LANGUAGES } from '../types';
import { Languages, Loader2, Copy, Check } from 'lucide-react';

interface TranslationHelperProps {
  currentText: string;
  currentLanguage: string;
  onTranslationComplete: (translatedText: string, targetLanguage: string) => void;
  disabled?: boolean;
  allLanguageData?: Record<string, string>; // All language versions of this field
}

export const TranslationHelper: React.FC<TranslationHelperProps> = ({
  currentText,
  currentLanguage,
  onTranslationComplete,
  disabled = false,
  allLanguageData = {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState<string>('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedText, setTranslatedText] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [copied, setCopied] = useState(false);

  // Reset state when current text changes
  useEffect(() => {
    setTranslatedText('');
    setError('');
    setCopied(false);
  }, [currentText, currentLanguage]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleTranslate = async () => {
    if (!targetLanguage) {
      setError('Please select a target language');
      return;
    }

    if (targetLanguage === currentLanguage) {
      setError('Target language must be different from current language');
      return;
    }

    // Find the best source text to translate from
    let sourceText = '';
    let sourceLanguage = '';
    
    // First, try to find a non-empty text in a different language
    for (const [langCode, text] of Object.entries(allLanguageData)) {
      if (langCode !== targetLanguage && text && text.trim()) {
        sourceText = text;
        sourceLanguage = langCode;
        break;
      }
    }
    
    // If no other language has text, use current text if it's not empty
    if (!sourceText && currentText.trim()) {
      sourceText = currentText;
      sourceLanguage = currentLanguage;
    }
    
    if (!sourceText) {
      setError('Please enter text in at least one language before translating');
      return;
    }

    setIsTranslating(true);
    setError('');

    try {
      const response = await apiService.translateText({
        text: sourceText,
        sourceLanguage: sourceLanguage,
        targetLanguage: targetLanguage,
      });

      if (response.success) {
        setTranslatedText(response.data.translatedText);
      } else {
        setError('Translation failed. Please try again.');
      }
    } catch (err) {
      console.error('Translation error:', err);
      setError('Translation failed. Please check your connection and try again.');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleApplyTranslation = () => {
    if (translatedText) {
      onTranslationComplete(translatedText, targetLanguage);
      setIsOpen(false);
      setTranslatedText('');
      setError('');
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(translatedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const availableLanguages = Object.entries(SUPPORTED_LANGUAGES).filter(
    ([code]) => code !== currentLanguage
  );

  if (!currentText.trim()) {
    return null;
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
        title="Translate this text"
      >
        <Languages className="h-3 w-3 mr-1" />
        Translate
      </button>

      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsOpen(false)}
        >
          <div 
            className="relative w-96 max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-lg p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900">AI Translation</h4>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Target Language
              </label>
              <select
                value={targetLanguage}
                onChange={(e) => setTargetLanguage(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-blue-500 focus:border-blue-500"
                disabled={isTranslating}
              >
                <option value="">Select language...</option>
                {availableLanguages.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Source Text
              </label>
              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded border max-h-20 overflow-y-auto">
                {(() => {
                  // Find the best source text to show
                  let sourceText = '';
                  let sourceLanguage = '';
                  
                  for (const [langCode, text] of Object.entries(allLanguageData)) {
                    if (langCode !== targetLanguage && text && text.trim()) {
                      sourceText = text;
                      sourceLanguage = langCode;
                      break;
                    }
                  }
                  
                  if (!sourceText && currentText.trim()) {
                    sourceText = currentText;
                    sourceLanguage = currentLanguage;
                  }
                  
                  return sourceText ? (
                    <>
                      <div className="text-xs text-blue-600 font-medium mb-1">
                        {SUPPORTED_LANGUAGES[sourceLanguage as keyof typeof SUPPORTED_LANGUAGES]}
                      </div>
                      {sourceText}
                    </>
                  ) : (
                    'No source text available'
                  );
                })()}
              </div>
            </div>

            <button
              type="button"
              onClick={handleTranslate}
              disabled={!targetLanguage || isTranslating}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-xs py-2 px-3 rounded-md font-medium flex items-center justify-center"
            >
              {isTranslating ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Translating...
                </>
              ) : (
                'Translate'
              )}
            </button>

            {error && (
              <div className="text-xs text-red-600 bg-red-50 p-2 rounded border">
                {error}
              </div>
            )}

            {translatedText && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-medium text-gray-600">
                    Translation ({SUPPORTED_LANGUAGES[targetLanguage as keyof typeof SUPPORTED_LANGUAGES]})
                  </label>
                  <button
                    type="button"
                    onClick={handleCopyToClipboard}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3 w-3 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <div className="text-xs text-gray-800 bg-blue-50 p-2 rounded border max-h-20 overflow-y-auto">
                  {translatedText}
                </div>
                <button
                  type="button"
                  onClick={handleApplyTranslation}
                  className="w-full bg-green-600 hover:bg-green-700 text-white text-xs py-2 px-3 rounded-md font-medium"
                >
                  Apply Translation
                </button>
              </div>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
